import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { colorScope } from '@/features/storefront/appearance';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import type { ProductDetailQueryFactory } from '@/features/storefront/sections/LookbookSection';
import {
  spotlightFromDetail,
  type SpotlightProduct,
} from '@/features/storefront/sections/ProductSpotlightSection';
import type { ProductShowcaseSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * A dark band: a big product scene on one side, a heading, a line of copy and
 * a button on the other, and a small card for one product overlapping the
 * scene's inner corner.
 *
 * The product is the one the merchant picked - or, until they pick one, the
 * shop's newest - and it is read the way the spotlight reads it (see
 * `ProductSpotlightSection.tsx`), so the card offers "Add to cart" for a
 * product sold in one form, in stock, where the shop has a cart, and sends
 * the shopper to choose anywhere else.
 *
 * The card is painted in the page's own colours rather than the band's, so it
 * stands out of the dark band whatever the palette is.
 *
 * With no scene the customiser keeps a surface-coloured block where it will
 * go; the shop drops the column and shows the words and the card on their own.
 */

export interface ProductShowcaseViewProps {
  section: ProductShowcaseSection;
  /** The customiser: keeps the empty scene as the place the picture will go. */
  preview: boolean;
  /** The card's product; null draws no card. */
  product: SpotlightProduct | null;
  productHref: (slug: string) => string;
  /** A link the way the renderer draws one: plain text in the customiser. */
  link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => ReactNode;
  /** The eyebrow, heading, copy and button, drawn by the renderer so they can be typed into. */
  words: ReactNode;
  /** Puts the one variant in the cart. Unset: the card links only. */
  onAdd?: (variantId: string) => void;
  adding?: boolean;
  added?: boolean;
}

/** The page's own colours, over the band's - see `colorScope`. */
const CARD_COLORS = colorScope('background', 'text') ?? {};

/** How many categories the pills show when the merchant has picked none. */
const DEFAULT_PILL_COUNT = 6;

/**
 * The categories a showcase's pills link to: the merchant's picks in their
 * order, skipping any since deleted - or, with none picked, the shop's first
 * six top-level categories. Empty while the pills are off.
 */
export function showcasePillCategories(
  section: ProductShowcaseSection,
  categories: StorefrontCategory[],
): StorefrontCategory[] {
  if (!section.show_category_pills) return [];
  if (section.pill_category_ids.length === 0) {
    return categories.filter((category) => !category.parent_id).slice(0, DEFAULT_PILL_COUNT);
  }
  const byId = new Map(categories.map((category) => [category.id, category]));
  return section.pill_category_ids
    .map((id) => byId.get(id))
    .filter((category): category is StorefrontCategory => Boolean(category));
}

/** A pill outlined in the band's own text colour, or filled like the shop's buttons. */
function pillStyle(style: ProductShowcaseSection['pill_style']): CSSProperties {
  return style === 'filled'
    ? { ...STOREFRONT_BUTTON_STYLE, borderRadius: '9999px' }
    : {
        border: '1px solid color-mix(in srgb, currentColor 35%, transparent)',
        borderRadius: '9999px',
      };
}

/**
 * A row of category pills above the showcase, each a link to that category's
 * listing. Wraps on a wide screen; on a phone it is one line that scrolls
 * sideways inside the band, so the page itself never grows wider.
 */
export function ShowcasePills({
  categories,
  pillStyle: style,
  link,
}: {
  categories: StorefrontCategory[];
  pillStyle: ProductShowcaseSection['pill_style'];
  link: ProductShowcaseViewProps['link'];
}) {
  if (categories.length === 0) return null;
  return (
    <div
      data-showcase-pills={style}
      className="-mx-5 mb-8 overflow-x-auto px-5 [scrollbar-width:none] sm:-mx-8 sm:px-8 lg:mx-0 lg:overflow-visible lg:px-0"
    >
      <ul className="flex w-max gap-2 lg:w-auto lg:flex-wrap">
        {categories.map((category) => (
          <li key={category.id} className="shrink-0">
            {link(
              `/products?category=${encodeURIComponent(category.slug)}`,
              'inline-flex h-9 items-center whitespace-nowrap px-4 text-sm font-medium',
              category.name,
              pillStyle(style),
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ProductShowcaseView({
  section,
  preview,
  product,
  productHref,
  link,
  words,
  onAdd,
  adding,
  added,
}: ProductShowcaseViewProps) {
  const scene = Boolean(section.image_url) || preview;
  const right = section.image_side === 'right';
  const card =
    section.show_card && product ? (
      <ShowcaseCard
        product={product}
        productHref={productHref}
        link={link}
        onAdd={onAdd}
        adding={adding}
        added={added}
      />
    ) : null;

  if (!scene) {
    return (
      <div
        data-showcase={section.image_side}
        className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16"
      >
        <div>{words}</div>
        {card ? <div className="w-full sm:w-72">{card}</div> : null}
      </div>
    );
  }

  return (
    <div
      data-showcase={section.image_side}
      className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
    >
      <div className={cn('relative', right && 'lg:order-2')}>
        {section.image_url ? (
          <StorefrontImage
            url={section.image_url}
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt=""
            data-showcase-scene
            className="aspect-[4/5] w-full object-cover sm:aspect-[4/3] lg:aspect-[4/5]"
            style={{ borderRadius: 'var(--storefront-radius)', ...focalStyle(section.image_focal) }}
          />
        ) : (
          <div
            aria-hidden
            data-showcase-scene
            className="aspect-[4/5] w-full sm:aspect-[4/3] lg:aspect-[4/5]"
            style={{ borderRadius: 'var(--storefront-radius)', backgroundColor: 'var(--storefront-surface)' }}
          />
        )}
        {card ? (
          // Stacked under the scene on a phone; on a wide screen it overlaps
          // the scene's inner corner, half into the gap beside it.
          <div
            data-showcase-card-position={section.card_position}
            className={cn(
              'relative z-10 mt-4 w-full sm:w-72 lg:absolute lg:mt-0 lg:w-68',
              section.card_position === 'top' ? 'lg:top-6' : 'lg:bottom-6',
              right ? 'lg:-left-8' : 'lg:-right-8',
            )}
          >
            {card}
          </div>
        ) : null}
      </div>
      <div className={cn(right && 'lg:order-1')}>{words}</div>
    </div>
  );
}

function ShowcaseCard({
  product,
  productHref,
  link,
  onAdd,
  adding,
  added,
}: Pick<ProductShowcaseViewProps, 'productHref' | 'link' | 'onAdd' | 'adding' | 'added'> & {
  product: SpotlightProduct;
}) {
  const copy = useShopContent();
  const href = productHref(product.slug);
  const onSale =
    product.compareAtPrice !== null && Number(product.compareAtPrice) > Number(product.price);
  const canAdd = Boolean(onAdd && product.variantId && product.inStock);
  const button = 'mt-3 inline-flex h-10 w-full items-center justify-center px-4 text-sm font-semibold';

  return (
    <div
      data-showcase-card
      className="p-3 shadow-xl"
      style={{ ...CARD_COLORS, borderRadius: 'var(--storefront-radius)' }}
    >
      <div className="flex items-center gap-3">
        {product.imageUrl ? (
          <StorefrontImage
            url={product.imageUrl}
            sizes="64px"
            alt=""
            className="size-16 shrink-0 object-cover"
            style={{ borderRadius: 'var(--storefront-radius)', ...focalStyle(product.imageFocal) }}
          />
        ) : (
          <div
            aria-hidden
            className="size-16 shrink-0"
            style={{ borderRadius: 'var(--storefront-radius)', backgroundColor: 'var(--storefront-surface)' }}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold leading-snug">
            {link(href, '', product.name)}
          </p>
          <p className="mt-1 flex items-baseline gap-2 text-sm">
            <span className="font-semibold">{formatMoney(product.price)}</span>
            {onSale ? (
              <span className="text-xs line-through" style={{ color: 'var(--storefront-muted)' }}>
                {formatMoney(product.compareAtPrice)}
              </span>
            ) : null}
          </p>
        </div>
      </div>
      {canAdd && product.variantId ? (
        <button
          type="button"
          disabled={adding}
          onClick={() => onAdd?.(product.variantId as string)}
          className={cn(button, 'disabled:opacity-60')}
          style={STOREFRONT_BUTTON_STYLE}
        >
          {added ? copy('account.added_to_cart') : copy('account.add_to_cart')}
        </button>
      ) : (
        link(href, button, copy('section.spotlight_choose'), STOREFRONT_BUTTON_STYLE)
      )}
    </div>
  );
}

/**
 * The showcase with its card's product fetched in full - so it knows whether
 * there is one variant to add - falling back to the catalog summary meanwhile.
 */
export function FetchedProductShowcase({
  query,
  slug,
  summary,
  quickAdd,
  ...props
}: Omit<ProductShowcaseViewProps, 'product' | 'onAdd' | 'adding' | 'added'> & {
  query: ProductDetailQueryFactory;
  slug: string;
  summary: SpotlightProduct | null;
  /** Whether the shop has a cart to add to. */
  quickAdd: boolean;
}) {
  const detail = useQuery({ ...query(slug), retry: false, staleTime: 60_000 });
  const add = useAddToCart();
  const product = detail.data ? spotlightFromDetail(detail.data) : summary;
  return (
    <ProductShowcaseView
      {...props}
      product={product}
      onAdd={quickAdd ? (variantId) => add.mutate({ variantId }) : undefined}
      adding={add.isPending}
      added={add.isSuccess}
    />
  );
}

/** What the showcase slider needs: its words, its pills, and the products it pages through. */
export interface ShowcaseCarouselProps {
  section: ProductShowcaseSection;
  /** The panel's colour - the section's own band colour, painted as a card. */
  fill: string;
  /** The eyebrow and heading, typeable in the customiser. */
  heading: ReactNode;
  /** The button under the slider, when the merchant wrote one. */
  cta?: ReactNode;
  categories: StorefrontCategory[];
  activeCategory: string | null;
  onPickCategory: (id: string) => void;
  products: SpotlightProduct[];
  loading?: boolean;
  productHref: (slug: string) => string;
  link: ProductShowcaseViewProps['link'];
  /** The customiser keeps an empty scene as the place the picture will go. */
  preview: boolean;
}

/** How much a sale takes off, as a whole percent; null when it is not a sale. */
export function percentOff(product: Pick<SpotlightProduct, 'price' | 'compareAtPrice'>): number | null {
  const price = Number(product.price);
  const was = product.compareAtPrice === null ? NaN : Number(product.compareAtPrice);
  if (!Number.isFinite(was) || !(was > price) || was <= 0) return null;
  return Math.round(((was - price) / was) * 100);
}

const ROUND = 'max(var(--storefront-radius), 1rem)';

/**
 * The slider layout: the heading and the category pills on the page, then a
 * rounded panel of the band's colour holding the big scene and one product at
 * a time on a light card, paged with two arrows. A pill picks which
 * category's products the slider pages through - it never leaves the page.
 */
export function ProductShowcaseCarousel({
  section,
  fill,
  heading,
  cta,
  categories,
  activeCategory,
  onPickCategory,
  products,
  loading,
  productHref,
  link,
  preview,
}: ShowcaseCarouselProps) {
  const copy = useShopContent();
  const [index, setIndex] = useState(0);
  const count = products.length;
  // A new category, or a shorter list, starts again from its first product.
  useEffect(() => setIndex(0), [activeCategory, count]);
  const product = count > 0 ? products[Math.min(index, count - 1)] : null;
  const off = product ? percentOff(product) : null;
  const scene = Boolean(section.image_url) || preview;
  const step = (by: number) => setIndex((current) => (current + by + count) % count);

  return (
    <div data-showcase-layout="carousel">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-10" style={CARD_COLORS}>
        {/* The heading keeps its line; the pills wrap beside it instead. */}
        <div className="min-w-0 lg:shrink-0">{heading}</div>
        {categories.length > 0 ? (
          <div className="-mx-5 min-w-0 overflow-x-auto px-5 [scrollbar-width:none] sm:-mx-8 sm:px-8 lg:mx-0 lg:flex-1 lg:px-0">
            <ul className="flex w-max gap-2 lg:w-auto lg:flex-wrap lg:justify-end" data-showcase-pills="tabs">
              {categories.map((category) => {
                const on = category.id === activeCategory;
                return (
                  <li key={category.id} className="shrink-0">
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => onPickCategory(category.id)}
                      className="inline-flex h-10 items-center whitespace-nowrap rounded-full border px-5 text-sm font-semibold transition-colors"
                      style={
                        on
                          ? { ...STOREFRONT_BUTTON_STYLE, borderRadius: '9999px' }
                          : { borderColor: 'color-mix(in srgb, var(--storefront-text) 70%, transparent)' }
                      }
                    >
                      {category.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div
        data-showcase-panel=""
        className={cn('grid gap-6 p-5 sm:p-8 lg:gap-8', scene && 'lg:grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)]')}
        style={{ backgroundColor: fill, borderRadius: 'max(var(--storefront-radius), 1.5rem)' }}
      >
        {scene ? (
          // A frame of its own height - the slider beside it decides the
          // row's - with the picture cropped to fill it, whatever its shape.
          <div
            className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[30rem]"
            style={{ borderRadius: ROUND, backgroundColor: 'var(--storefront-surface)' }}
            data-showcase-scene
          >
            {section.image_url ? (
              <StorefrontImage
                url={section.image_url}
                sizes="(min-width: 1024px) 66vw, 100vw"
                alt=""
                className="absolute inset-0 size-full object-cover"
                style={focalStyle(section.image_focal)}
              />
            ) : null}
          </div>
        ) : null}

        <div className="flex min-w-0 flex-col justify-center" aria-live="polite" data-showcase-slider="">
          {product ? (
            <>
              <div className="overflow-hidden" style={{ ...CARD_COLORS, borderRadius: ROUND }}>
                <div className="relative">
                  {product.imageUrl ? (
                    <StorefrontImage
                      url={product.imageUrl}
                      sizes="(min-width: 1024px) 24rem, 100vw"
                      alt=""
                      className="aspect-square w-full object-cover"
                      style={focalStyle(product.imageFocal)}
                    />
                  ) : (
                    <div aria-hidden className="aspect-square w-full" style={{ backgroundColor: 'var(--storefront-surface)' }} />
                  )}
                  {off !== null ? (
                    <span
                      className="absolute start-3 top-3 rounded-full px-3 py-1 text-xs font-semibold"
                      style={{ ...CARD_COLORS, color: 'var(--storefront-primary)' }}
                      data-showcase-off=""
                    >
                      -{off}%
                    </span>
                  ) : null}
                </div>
                <p className="px-4 py-4 text-center text-sm uppercase tracking-[0.12em]">
                  {link(productHref(product.slug), 'hover:underline', product.name)}
                </p>
              </div>
              <p className="mt-4 flex items-baseline justify-center gap-2 font-semibold">
                <span>{formatMoney(product.price)}</span>
                {off !== null ? (
                  <span className="text-sm font-normal line-through opacity-70">
                    {formatMoney(product.compareAtPrice)}
                  </span>
                ) : null}
              </p>
              {count > 1 ? (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label={copy('common.rail_previous', { label: section.heading })}
                    className="inline-flex size-10 items-center justify-center rounded-full bg-white text-[#0b1220] shadow transition-transform hover:scale-105"
                  >
                    <ChevronLeft className="rtl-flip size-5" aria-hidden />
                  </button>
                  <span className="text-xs opacity-80" data-showcase-count="">
                    {Math.min(index, count - 1) + 1} / {count}
                  </span>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label={copy('common.rail_more', { label: section.heading })}
                    className="inline-flex size-10 items-center justify-center rounded-full bg-white text-[#0b1220] shadow transition-transform hover:scale-105"
                  >
                    <ChevronRight className="rtl-flip size-5" aria-hidden />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-center text-sm opacity-80">
              {loading ? copy('common.loading') : copy('listing.empty')}
            </p>
          )}
          {cta ? <div className="mt-2 text-center">{cta}</div> : null}
        </div>
      </div>
    </div>
  );
}
